import {useCallback, useState, type ReactNode} from 'react';
import styled from 'styled-components';
import {Switch, Route, Redirect, Link, matchPath, useLocation} from 'react-router-dom';
import {graphql, ForecastQueryRenderer} from '../../../graphql/forecastRelay';
import type {SettingsRouteQuery as SettingsRouteQueryType} from './__generated__/SettingsRouteQuery.graphql';
import {SettingsProvider, type SettingsContextValue} from './SettingsContext';
import {SettingsGeneralTab} from './SettingsGeneralTab';
import {SettingsEmailTab} from './SettingsEmailTab';
import {SettingsCustomFieldsTab} from './SettingsCustomFieldsTab';

const SettingsRouteQueryDoc = graphql`
	query SettingsRouteQuery {
		viewer {
			actualPersonId
			account {
				id
				...SettingsGeneralTab_account
				...SettingsEmailTab_account
			}
		}
	}
`;

const Container = styled.div`
	margin: 0 auto;
	max-width: 64rem;
	padding: 24px;
`;
const Title = styled.h1`
	font-size: 24px;
	line-height: 32px;
	font-weight: 700;
	color: #0f172a;
	margin: 0;
`;
const TabNav = styled.nav`
	margin-top: 24px;
	display: flex;
	gap: 8px;
	border-bottom: 1px solid #e2e8f0;
`;
const TabLinkStyled = styled(Link)<{$active: boolean}>`
	border-bottom: 2px solid ${p => (p.$active ? '#2563eb' : 'transparent')};
	padding: 8px 16px;
	font-size: 14px;
	line-height: 20px;
	font-weight: 500;
	text-decoration: none;
	color: ${p => (p.$active ? '#1d4ed8' : '#64748b')};
	&:hover {
		color: ${p => (p.$active ? '#1d4ed8' : '#334155')};
	}
`;
const TabContent = styled.div`
	margin-top: 24px;
`;
const Loading = styled.div`
	padding: 16px;
	color: #64748b;
`;

function TabLink({to, children}: {to: string; children: ReactNode}) {
	const location = useLocation();
	const active = matchPath(location.pathname, {path: to}) != null;
	return (
		<TabLinkStyled to={to} $active={active}>
			{children}
		</TabLinkStyled>
	);
}

function SettingsChrome({ctx, basePath}: {ctx: SettingsContextValue; basePath: string}) {
	return (
		<Container>
			<Title>Settings</Title>
			<TabNav>
				<TabLink to={`${basePath}/general`}>General</TabLink>
				<TabLink to={`${basePath}/email`}>Email</TabLink>
				<TabLink to={`${basePath}/custom-fields`}>Custom Fields</TabLink>
			</TabNav>
			<TabContent>
				<SettingsProvider value={ctx}>
					<Switch>
						<Redirect exact from={basePath} to={`${basePath}/general`} />
						<Route path={`${basePath}/general`}>
							<SettingsGeneralTab />
						</Route>
						<Route path={`${basePath}/email`}>
							<SettingsEmailTab />
						</Route>
						<Route path={`${basePath}/custom-fields`}>
							<SettingsCustomFieldsTab />
						</Route>
					</Switch>
				</SettingsProvider>
			</TabContent>
		</Container>
	);
}

export function SettingsRoute({basePath = '/tickets/settings'}: {basePath?: string} = {}) {
	const [fetchKey, setFetchKey] = useState(0);
	const refetch = useCallback(() => setFetchKey(k => k + 1), []);

	return (
		<ForecastQueryRenderer
			key={fetchKey}
			query={SettingsRouteQueryDoc}
			variables={{}}
			customLoader={() => <Loading>Loading…</Loading>}
			redirectIfNotLoggedIn
			render={(relayProps: SettingsRouteQueryType['response'] | null) => {
				if (!relayProps || !relayProps.viewer) return null;
				const ctx: SettingsContextValue = {
					account: relayProps.viewer.account,
					refetch,
				};
				return <SettingsChrome ctx={ctx} basePath={basePath} />;
			}}
		/>
	);
}
